/**
 * @fileoverview Manages feature creation and updates for tactical annotations.
 */

import type { Collection } from 'ol';
import { Feature } from 'ol';
import type { Coordinate } from 'ol/coordinate';
import type { Geometry, Polygon } from 'ol/geom';
import { LineString, Point } from 'ol/geom';
import type VectorSource from 'ol/source/Vector';

import type { NamedLatLonCoordinate } from '@nora-monorepo/shared';

import { namedLatLonCoordinateToOlCoordinate, olToNamedLatLonCoordinate } from '#nora-ui/map/drawtools/draw-tool-utils';

import { AREA_CORNERS, TacticalAnnotationCalculator } from './tactical-annotation-calculator';

/**
 * Data structure for a building side line.
 */
export interface LineData {
  start: NamedLatLonCoordinate;
  end: NamedLatLonCoordinate;
  index: number;
}

/**
 * Manages all features for a tactical annotation (lines, handles, indicators).
 */
export class TacticalAnnotationFeatureManager {
  private cornerHandles: Feature<Point>[] = [];
  private lineFeatures: Feature<Geometry>[] = [];
  private lines: LineData[] = [];
  private pointIndicators: Feature<Point>[] = [];

  constructor(
    private readonly vectorSource: VectorSource,
    private readonly featureId: string,
  ) {
  }

  // Getters
  getLineFeatures(): Feature<Geometry>[] {
    return this.lineFeatures;
  }

  getLines(): LineData[] {
    return this.lines;
  }

  getPointIndicators(): Feature<Point>[] {
    return this.pointIndicators;
  }

  /**
   * Creates line features for each side of the polygon.
   * Preserves side indices when updating to maintain color consistency.
   */
  createSideFeatures(
    polygonFeature: Feature<Polygon>,
    defaultProperties: Record<string, unknown>,
  ): void {
    const geometry = polygonFeature.getGeometry();
    if (!geometry) {
      return;
    }

    const coordinates = geometry.getCoordinates()[0];
    if (coordinates.length !== AREA_CORNERS + 1) {
      return;
    }

    // Store existing line info for index preservation
    const existingLines = this.lineFeatures.map((feature, index) => {
      const lineCoordinates = (feature.getGeometry() as LineString).getCoordinates();
      const sideIndex = feature.get('sideIndex') as number | undefined;

      return {
        feature,
        coords: lineCoordinates ?? [],
        index: sideIndex ?? index,
      };
    });

    // Remove old features
    for (const oldFeature of this.lineFeatures) {
      this.vectorSource.removeFeature(oldFeature);
    }
    this.lineFeatures = [];

    // Create new side features
    for (let i = 0; i < AREA_CORNERS; i++) {
      const startCoordinate = coordinates[i];
      const endCoordinate = coordinates[i + 1];
      const sideCoordinates: Coordinate[] = [startCoordinate, endCoordinate];
      const sideFeature = new Feature({ geometry: new LineString(sideCoordinates) });

      // Preserve index by matching with closest existing line
      let sideIndex = i;
      if (existingLines.length === AREA_CORNERS) {
        const { closestIndex, line } = TacticalAnnotationCalculator.findMatchingLineIndex(sideCoordinates, existingLines);
        sideIndex = line.index;

        // prevent duplicate matching in subsequent iterations
        existingLines.splice(closestIndex, 1);
      }

      sideFeature.setProperties({
        ...defaultProperties,
        id: `${this.featureId}-line-${i}`,
        sideIndex,
        originalPolygonIndex: i,
        zIndex: 1,
      });

      this.lineFeatures.push(sideFeature);
      this.vectorSource.addFeature(sideFeature);
    }
  }

  /**
   * Updates side feature geometries to match the polygon coordinates.
   */
  updateSideFeatures(polygonFeature: Feature<Polygon>): void {
    if (this.lineFeatures.length !== AREA_CORNERS) {
      return;
    }

    const geometry = polygonFeature.getGeometry();
    if (!geometry) {
      return;
    }

    const coordinates = geometry.getCoordinates()[0];

    for (const [i, lineFeature] of this.lineFeatures.entries()) {
      const polygonIndex = (lineFeature.get('originalPolygonIndex') as number | undefined) ?? i;
      const lineGeometry = lineFeature.getGeometry() as LineString;
      const startCoordinate = coordinates[polygonIndex];
      const endCoordinate = coordinates[polygonIndex + 1];
      lineGeometry.setCoordinates([
        startCoordinate,
        endCoordinate,
      ]);
    }
  }

  /**
   * Creates draggable corner handles at each polygon vertex.
   */
  createCornerHandles(polygonFeature: Feature<Polygon>): void {
    for (const handle of this.cornerHandles) {
      this.vectorSource.removeFeature(handle);
    }
    this.cornerHandles = [];

    const geometry = polygonFeature.getGeometry();
    if (!geometry) {
      return;
    }

    const coordinates = geometry.getCoordinates()[0];

    for (let i = 0; i < AREA_CORNERS; i++) {
      const coordinate = coordinates[i];
      const handleFeature = new Feature({ geometry: new Point(coordinate) });

      handleFeature.setProperties({
        preventSelection: true,
        isCornerHandle: true,
        type: 'move-handle',
        zIndex: 100,
      });

      this.cornerHandles.push(handleFeature);
      this.vectorSource.addFeature(handleFeature);
    }
  }

  /**
   * Updates corner handle positions to match the polygon geometry.
   */
  updateCornerHandles(polygonFeature: Feature<Polygon>): void {
    if (this.cornerHandles.length !== AREA_CORNERS) {
      return;
    }

    const geometry = polygonFeature.getGeometry();
    if (!geometry) {
      return;
    }

    const coordinates = geometry.getCoordinates()[0];

    for (const [i, handle] of this.cornerHandles.entries()) {
      const coordinate = coordinates[i];
      handle.getGeometry()?.setCoordinates(coordinate);
    }
  }

  /**
   * Adds a point indicator during the drawing phase.
   */
  addPointIndicator(coordinate: Coordinate, modifyFeatures: Collection<Feature<Geometry>>): Feature<Point> {
    const pointFeature = new Feature({ geometry: new Point(coordinate) });

    pointFeature.setProperties({
      preventSelection: true,
      isPointIndicator: true,
      type: 'point-indicator',
      zIndex: 50,
      pointIndex: this.pointIndicators.length,
    });

    this.pointIndicators.push(pointFeature);
    this.vectorSource.addFeature(pointFeature);
    modifyFeatures.push(pointFeature);

    return pointFeature;
  }

  /**
   * Removes all point indicators.
   */
  removePointIndicators(modifyFeatures: Collection<Feature<Geometry>>): void {
    for (const indicator of this.pointIndicators) {
      this.vectorSource.removeFeature(indicator);
      modifyFeatures.remove(indicator);
    }
    this.pointIndicators = [];
  }

  /**
   * Extracts line data from current line features.
   */
  updateLinesFromFeatures(): void {
    if (this.lineFeatures.length !== AREA_CORNERS) {
      return;
    }

    this.lines = this.lineFeatures.map((feature) => {
      const geometry = feature.getGeometry() as LineString;
      const coordinates = geometry.getCoordinates();
      const index = (feature.get('sideIndex') as number | undefined) ?? 0;

      if (coordinates.length < 2) {
        return {
          start: olToNamedLatLonCoordinate([0, 0]),
          end: olToNamedLatLonCoordinate([0, 0]),
          index,
        };
      }
      return {
        start: olToNamedLatLonCoordinate(coordinates[0]),
        end: olToNamedLatLonCoordinate(coordinates[1]),
        index,
      };
    });
  }

  /**
   * Reorders line features so the selected side becomes index 0.
   */
  reorderLinesToMakeIndexZero(selectedIndex: number): void {
    if (selectedIndex === 0 || this.lineFeatures.length !== AREA_CORNERS) {
      return;
    }

    // Rotate array so selected becomes first
    this.lineFeatures = [
      ...this.lineFeatures.slice(selectedIndex),
      ...this.lineFeatures.slice(0, selectedIndex),
    ];

    // Update indices
    for (const [newIndex, feature] of this.lineFeatures.entries()) {
      feature.set('sideIndex', newIndex);
      feature.set('id', `${this.featureId}-line-${newIndex}`);
    }
  }

  /**
   * Loads line features from saved tactical annotation data.
   */
  loadLinesFromData(
    lines: Array<{ start: NamedLatLonCoordinate; end: NamedLatLonCoordinate; index: number }>,
    polygonCoords: Coordinate[],
    defaultProperties: Record<string, unknown>,
  ): void {
    this.lines = [...lines].sort((a, b) => a.index - b.index);

    for (const line of this.lines) {
      const startCoord = namedLatLonCoordinateToOlCoordinate(line.start);
      const endCoord = namedLatLonCoordinateToOlCoordinate(line.end);

      const { closestIndex } = TacticalAnnotationCalculator.findClosestEdgeIndex(
        startCoord,
        endCoord,
        polygonCoords,
      );

      const sideFeature = new Feature({
        geometry: new LineString([startCoord, endCoord]),
      });

      sideFeature.setProperties({
        ...defaultProperties,
        id: `${this.featureId}-line-${line.index}`,
        sideIndex: line.index,
        originalPolygonIndex: closestIndex,
        zIndex: 1,
      });

      this.lineFeatures.push(sideFeature);
      this.vectorSource.addFeature(sideFeature);
    }
  }

  /**
   * Clears all features
   */
  clear(modifyFeatures: Collection<Feature<Geometry>>): void {
    for (const feature of this.lineFeatures) {
      this.vectorSource.removeFeature(feature);
    }
    for (const handle of this.cornerHandles) {
      this.vectorSource.removeFeature(handle);
    }
    for (const indicator of this.pointIndicators) {
      this.vectorSource.removeFeature(indicator);
      modifyFeatures.remove(indicator);
    }

    this.lineFeatures = [];
    this.cornerHandles = [];
    this.pointIndicators = [];
    this.lines = [];
  }
}
